import type { Optional } from '@vimeo/core/types/generic';

import { EventEmitter } from './EventEmitter';
import { IntegrationLogger } from './IntegrationLogger';
import type { ConfigStorageService } from './services/ConfigStorageService';
import type { TokenService } from './services/TokenService';
import type { WindowMessagingService } from './services/WindowMessagingService';
import { EIntegrationCrossWindowEvents } from './shared/crossWindowMessaging';
import type { TIntegrationConfigUnionPartial } from './shared/integration';

// eslint-disable-next-line typescript-enum/no-enum
export enum EIntegrationCommonEvents {
  CONFIG = 'config',
  TOKEN_REFRESH = 'tokenrefresh',
}

// TODO add unit tests
// TODO add jsdoc in places where it makes sense when implementation is finished/stable
export class Integration<
  Config extends TIntegrationConfigUnionPartial = TIntegrationConfigUnionPartial,
> {
  private readonly log: IntegrationLogger = IntegrationLogger.create('Integration');
  private readonly eventEmitter: EventEmitter<{
    [EIntegrationCommonEvents.CONFIG]: [Config];
    [EIntegrationCommonEvents.TOKEN_REFRESH]: [string];
  }> = new EventEmitter();

  private _isDestroyed: boolean = false;
  private config: Optional<Config> = null;
  private loadConfigPromise: Optional<Promise<Config>> = null;

  constructor(
    protected readonly messengerService: WindowMessagingService,
    protected readonly storageService: ConfigStorageService<Config>,
    protected readonly tokenService: TokenService,
  ) {
    this.handleTokenRefresh = this.handleTokenRefresh.bind(this);
  }

  public destroy(): void {
    if (this._isDestroyed) {
      return;
    }
    this.eventEmitter.removeAllListeners();
    this.stopTokenRotation();
    this.messengerService.destroy();
    this._isDestroyed = true;
  }

  public isDestroyed(): boolean {
    return this._isDestroyed;
  }

  public loadConfig(): Promise<Config> {
    this.assertNotDestroyed('loadConfig');
    if (this.loadConfigPromise) {
      return this.loadConfigPromise;
    }
    this.loadConfigPromise = (async () => {
      try {
        this.tryToLoadConfigFromStorage();
        if (!this.config) {
          await this.loadConfigFromHostWindow();
        }
        return this.config as Config;
      } finally {
        this.loadConfigPromise = null;
      }
    })();
    return this.loadConfigPromise;
  }

  public startTokenRotation(): void {
    this.assertNotDestroyed('startTokenRotation');
    this.tokenService.onTokenRefresh(this.handleTokenRefresh);
    this.tokenService.startTokenAutoRefresh();
  }

  public stopTokenRotation(): void {
    this.assertNotDestroyed('stopTokenRotation');
    this.tokenService.offTokenRefresh(this.handleTokenRefresh);
    this.tokenService.stopTokenAutoRefresh();
  }

  public async forceTokenRefresh(): Promise<Optional<string>> {
    this.assertNotDestroyed('forceTokenRefresh');
    return this.tokenService.forceTokenRefresh();
  }

  public on(event: `${EIntegrationCommonEvents.CONFIG}`, handler: (config: Config) => void): void;
  public on(
    event: `${EIntegrationCommonEvents.TOKEN_REFRESH}`,
    handler: (newAccessToken: string) => void,
  ): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public on(event: `${EIntegrationCommonEvents}`, handler: (...args: any[]) => void): void {
    this.assertNotDestroyed('on');
    this.eventEmitter.on(event as EIntegrationCommonEvents, handler);
  }

  public once(event: `${EIntegrationCommonEvents.CONFIG}`, handler: (config: Config) => void): void;
  public once(
    event: `${EIntegrationCommonEvents.TOKEN_REFRESH}`,
    handler: (newAccessToken: string) => void,
  ): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public once(event: `${EIntegrationCommonEvents}`, handler: (...args: any[]) => void): void {
    this.assertNotDestroyed('once');
    this.eventEmitter.once(event as EIntegrationCommonEvents, handler);
  }

  public off(event: `${EIntegrationCommonEvents.CONFIG}`, handler: (config: Config) => void): void;
  public off(
    event: `${EIntegrationCommonEvents.TOKEN_REFRESH}`,
    handler: (newAccessToken: string) => void,
  ): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public off(event: `${EIntegrationCommonEvents}`, handler: (...args: any[]) => void): void {
    if (this._isDestroyed) {
      return;
    }
    this.eventEmitter.off(event as EIntegrationCommonEvents, handler);
  }

  private handleTokenRefresh(newAccessToken: string): void {
    this.config = {
      ...this.config,
      accessToken: newAccessToken,
    } as Config;
    this.storageService.saveConfig(this.config);
    this.eventEmitter.emit(EIntegrationCommonEvents.TOKEN_REFRESH, newAccessToken);
    this.eventEmitter.emit(EIntegrationCommonEvents.CONFIG, { ...this.config });
  }

  private tryToLoadConfigFromStorage(): void {
    const configFromStorage = this.storageService.loadConfig();
    if (configFromStorage) {
      this.config = configFromStorage;
      this.setAccessTokens();
      this.eventEmitter.emit(EIntegrationCommonEvents.CONFIG, { ...configFromStorage });
    } else {
      this.log.info('no config found in storage');
    }
  }

  private async loadConfigFromHostWindow(): Promise<void> {
    const [isConfigRetrieved, configFromHostOrError] =
      await this.messengerService.sendMessageWithResponse<Config>(
        EIntegrationCrossWindowEvents.REQUEST_CONFIG,
      );
    if (isConfigRetrieved) {
      this.log.info('config loaded from host');
      this.config = configFromHostOrError;
      this.storageService.saveConfig(configFromHostOrError);
      this.setAccessTokens();
      this.eventEmitter.emit(EIntegrationCommonEvents.CONFIG, { ...configFromHostOrError });
    } else {
      if (!this._isDestroyed) {
        this.throwError(`failed to load config from host: ${String(configFromHostOrError)}`);
      }
    }
  }

  private setAccessTokens(): void {
    if (!this.config?.accessToken) {
      this.log.error('failed to set accessTokens for tokenService');
      return;
    }
    // TODO start passing 'accessToken' and 'refreshToken' params when refresh functionality is ready
    //  or delete refreshToken from codebase at all if decided not to implement
    // this.tokenService.setTokens(this.config.accessToken, this.config.refreshToken);
  }

  private assertNotDestroyed(invokingMethod: keyof this): void {
    if (this._isDestroyed) {
      this.throwError(`Cannot call ${String(invokingMethod)}() on destroyed Integration instance`);
    }
  }

  private throwError(errorText: string): never {
    this.log.error(errorText);
    throw new Error(errorText);
  }
}
